import React, { useState } from "react";
import { Copy, Pencil, Trash2, Eye, EyeOff, Archive, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImg } from "../../../lib/api";
import { errText } from "../biz/i18n";
import { Card, Empty, btnMain, btnSoft } from "../biz/ui";
import { useLabT } from "./i18n";
import { StatusPill } from "./fields";
import { ExperimentForm } from "./ExperimentForm";

export const useLabActions = (base, reload) => {
  const { t } = useLabT();
  const run = async (fn, msg) => {
    try { await fn(); toast.success(t(msg)); reload(); } catch (e) { toast.error(errText(e)); }
  };
  return {
    patch: (x, body) => run(() => api.put(`/admin/lab/${base}/${x.id}`, body), "saved"),
    duplicate: (x) => run(() => api.post(`/admin/lab/${base}/${x.id}/duplicate`), "duplicated"),
    remove: (x) => window.confirm(t("confirm_delete")) && run(() => api.delete(`/admin/lab/${base}/${x.id}`), "deleted"),
  };
};

export const RowActions = ({ x, t, act, onEdit, stockKey, prefix }) => (
  <div className="flex flex-wrap gap-2 mt-4">
    <button onClick={onEdit} className={btnSoft} data-testid={`${prefix}-edit-${x.id}`}><Pencil size={14} />{t("edit")}</button>
    <button onClick={() => act.duplicate(x)} className={btnSoft} data-testid={`${prefix}-dup-${x.id}`}><Copy size={14} />{t("duplicate")}</button>
    <button onClick={() => act.patch(x, { public: !x.public })} className={btnSoft} data-testid={`${prefix}-pub-${x.id}`}>
      {x.public ? <EyeOff size={14} /> : <Eye size={14} />}{x.public ? t("unpublish") : t("publish")}</button>
    {x.status !== "archived" && <button onClick={() => act.patch(x, { status: "archived" })} className={btnSoft} data-testid={`${prefix}-archive-${x.id}`}><Archive size={14} />{t("archive")}</button>}
    {!x.locked && (
      <button onClick={() => act.remove(x)} className={`${btnSoft} !text-[#A33A2B]`} data-testid={`${prefix}-del-${x.id}`}><Trash2 size={14} />{t("delete")}</button>)}
    <span className="inline-flex items-center gap-2 ms-auto text-sm text-[#6B645A]">{t(stockKey)}
      <button className={btnSoft} onClick={() => act.patch(x, { [stockKeyField(stockKey)]: Math.max(0, (x[stockKeyField(stockKey)] || 0) - 1) })} data-testid={`${prefix}-stock-dec-${x.id}`}>−</button>
      <b dir="ltr" data-testid={`${prefix}-stock-${x.id}`}>{x[stockKeyField(stockKey)] || 0}</b>
      <button className={btnSoft} onClick={() => act.patch(x, { [stockKeyField(stockKey)]: (x[stockKeyField(stockKey)] || 0) + 1 })} data-testid={`${prefix}-stock-inc-${x.id}`}>+</button>
    </span>
  </div>
);
const stockKeyField = (k) => (k === "sets_stock" ? "sets_stock" : "stock");

export function LabExperiments({ data, reload }) {
  const { t, lang, aed } = useLabT();
  const [edit, setEdit] = useState(null);
  const act = useLabActions("experiments", reload);
  return (
    <div className="space-y-4">
      <button onClick={() => setEdit({})} className={btnMain} data-testid="lab-add-experiment"><Plus size={16} />{t("add_experiment")}</button>
      {!data.experiments.length && <Empty>{t("no_items")}</Empty>}
      {data.experiments.map((x) => (
        <Card key={x.id} testid={`lab-exp-${x.id}`}>
          <div className="flex gap-4">
            {x.images?.[0] && <img src={resolveImg(x.images[0])} alt="" className="w-16 h-20 object-cover rounded-lg" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap"><b dir="ltr">{x.code}</b><StatusPill t={t} status={x.status} />
                <span className="text-xs text-[#8A8173]">{x.public ? t("visible") : t("hidden")}</span></div>
              <p className="mt-1">{(lang === "ar" ? x.name_ar : x.name_en) || x.name_en || x.name_ar}</p>
              <p className="text-xs text-[#8A8173] mt-1" dir="ltr">{x.size_ml} ml · {aed(x.price)} · {t(x.eval_category)} · {x.orders_count} {t("orders")} · {x.evaluations_count} {t("evaluations")}</p>
            </div>
          </div>
          <RowActions x={x} t={t} act={act} onEdit={() => setEdit(x)} stockKey="stock" prefix="lab-exp" />
        </Card>
      ))}
      {edit && <ExperimentForm initial={edit} defaults={{ code: data.next_experiment_code, size: data.settings.default_size_ml }} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </div>
  );
}
